/** Debug snapshot published on `window.__georgi` for the Playwright tests and manual inspection. */
import type { LocomotionState } from '../player/actionGraph';
import type { Facing } from '../player/stateMachine';

export interface PlayerSnapshot {
  state: LocomotionState;
  facing: Facing;
  action: string;
  frame: number;
  /** Transition phase 0..1, null while a loop plays. */
  phase: number | null;
  queue: string[];
  x: number;
  feetY: number;
  vx: number;
  vy: number;
  grounded: boolean;
  coyoteMs: number;
  jumpBufferMs: number;
  body: { x: number; y: number; w: number; h: number };
  /** Which authored body is active: ground (idle/walk), run or air. */
  bodyFamily: 'ground' | 'run' | 'air';
  /** Runtime in-betweening: next frame and how much of it is drawn over the current one. */
  blend: { next: number; fraction: number };
  frameBlend: boolean;
}

export interface RoomSnapshot {
  id: string;
  width: number;
  height: number;
  respawns: number;
  spawn: { x: number; feetY: number };
  platforms: number;
}

export interface EndlessSnapshot {
  mode: 'title' | 'running' | 'paused' | 'gameover';
  seed: number;
  score: number;
  distanceM: number;
  bones: number;
  combo: number;
  multiplier: number;
  lives: number;
  level: { index: number; name: string };
  powerups: string[];
  /** Chunks generated so far and currently alive. */
  chunks: number;
  liveChunks: number;
  respawns: number;
  safeSpot: { x: number; y: number };
  best: { score: number; distanceM: number; level: number } | null;
  muted: boolean;
  /** Kind of the platform Georgi stands on this frame (null while airborne). */
  groundedOn: string | null;
  /** On-screen touch pad shown. */
  touch: boolean;
  /** This run uses today's shared daily seed. */
  daily: boolean;
  /** Rolling thorns alive. */
  rollers: number;
  /** Creatures (beetles, bats, rocks) alive. */
  creatures: number;
  /** Wind force acting on Georgi this frame (px/s²). */
  wind: number;
  /** Standing on ice. */
  onIce: boolean;
  /** Deaths of this session, for balancing. */
  deaths: { byReason: Record<string, number>; byPattern: Record<string, number> };
}

export interface GeorgiSnapshot {
  ready: boolean;
  phase: 'boot' | 'loading' | 'gallery' | 'room' | 'endless' | 'failed';
  tier: string;
  renderer: 'webgl' | 'canvas' | 'unknown';
  facing: Facing;
  rate: number;
  liftPreview: boolean;
  actions: { id: string; frames: number; textureHas: boolean; playing: boolean }[];
  renderedFrames: number;
  errors: string[];
  /** Number of N1 animations registered at boot. */
  registered: number;
  room: RoomSnapshot | null;
  endless: EndlessSnapshot | null;
  player: PlayerSnapshot | null;
  /** Locomotion states in order of entry (consecutive duplicates collapsed, capped). */
  stateHistory: string[];
  /** Actions (clips + loops) in order of first display (consecutive duplicates collapsed, capped). */
  actionHistory: string[];
  overlay: boolean;
  help: boolean;
  camera: { scrollX: number; scrollY: number };
}

declare global {
  interface Window {
    __georgi?: GeorgiSnapshot;
  }
}

export const HISTORY_CAP = 200;

export const snapshot: GeorgiSnapshot = {
  ready: false,
  phase: 'boot',
  tier: '',
  renderer: 'unknown',
  facing: 'left',
  rate: 1,
  liftPreview: false,
  actions: [],
  renderedFrames: 0,
  errors: [],
  registered: 0,
  room: null,
  endless: null,
  player: null,
  stateHistory: [],
  actionHistory: [],
  overlay: false,
  help: true,
  camera: { scrollX: 0, scrollY: 0 },
};

export function publishSnapshot(): void {
  if (typeof window !== 'undefined') window.__georgi = snapshot;
}

/** Appends when different from the last entry; keeps the newest HISTORY_CAP entries. */
export function pushHistory(list: string[], entry: string): void {
  if (list[list.length - 1] === entry) return;
  list.push(entry);
  if (list.length > HISTORY_CAP) list.splice(0, list.length - HISTORY_CAP);
}

export function recordError(message: string): void {
  snapshot.errors.push(message);
  snapshot.phase = 'failed';
  publishSnapshot();
}
